import { Fragment, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, User } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import Loader from '@/components/Loader';
import { genUniqueId } from '@/lib/utils';
import { ClientContactFormSection } from '@pages/clients/steppers/ClientContactFormSection';
import { ClientCreateRequest } from '@brainvaulttech/dgr-react-sdk';
import VendorContactFormSection from '@pages/vendors/VendorContactFormSection';

export type ContactDetails = {
  contactId?: string;
  firstName: string;
  lastName: string;
  type?: string;
  lawyerBarId?: string;
  email: string;
  fax?: string;
  phone?: string;
  extension?: string;
  cell?: string;
};

export type Contact = {
  id: string;
  name: string;
  data: ContactDetails;
};
export const ContactForm = ({
  mode,
  isFetching = false,
  contactFor,
}: {
  mode: 'create' | 'edit';
  isFetching?: boolean;
  contactFor: 'CLIENT' | 'VENDOR';
}) => {
  const [searchParam] = useSearchParams();
  const {
    getValues,
    watch,
    reset,
    setValue,
    trigger,
    formState: { isDirty },
  } = useFormContext();
  const [contacts, setContacts] = useState<Contact[]>([
    { id: genUniqueId(), name: 'Contact', data: {} as ContactDetails },
  ]);
  const [activeContactId, setActiveContactId] = useState<string>(contacts[0].id);
  const currentContactIndex = contacts.findIndex((contact) => contact.id === activeContactId);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const firstName = watch(`contacts[${currentContactIndex}].firstName`) as string;
  const lastName = watch(`contacts[${currentContactIndex}].lastName`) as string;
  const type = watch(`contacts[${currentContactIndex}].type`) as string;

  const [isLoading, setisLoading] = useState(mode === 'edit');
  useEffect(() => {
    if (type !== 'LAWYER') {
      setValue(`contacts[${currentContactIndex}].lawyerBarId`, null, {
        shouldDirty: false,
      });
    }
  }, [type]);

  useEffect(() => {
    const updatedContacts = contacts.map((contact) =>
      contact.id === activeContactId
        ? {
            ...contact,
            name:
              firstName && lastName
                ? `${firstName} ${lastName}`
                : firstName
                  ? firstName
                  : lastName
                    ? lastName
                    : contact.name,
          }
        : contact
    );
    setContacts(updatedContacts);
  }, [firstName, lastName, activeContactId]);

  const handleContactSwitch = (id: string) => {
    setActiveContactId(id);
  };

  useEffect(() => {
    setTimeout(() => {
      setisLoading(false);
    }, 400);
  }, []);

  const addContact = () => {
    const newContactId = genUniqueId();
    setContacts([...contacts, { id: newContactId, name: `New Contact`, data: {} as ContactDetails }]);
    setActiveContactId(newContactId);
  };
  const resetDataExchangeEmails = async () => {
    const values = getValues() as ClientCreateRequest;
    const emails = values.contacts?.map((contact) => contact.email) ?? [];
    reset(
      {
        ...values,
        affidavit: values.affidavit?.filter((email: string) => emails.some((e) => e === email)),
        arInvoices: values.arInvoices?.filter((email: string) => emails.some((e) => e === email)),
        finalBillingSummary: values.finalBillingSummary?.filter((email: string) => emails.some((e) => e === email)),
        recapAndInvoice: values.recapAndInvoice?.filter((email: string) => emails.some((e) => e === email)),
        affidavitWithInvoice: values.affidavitWithInvoice?.filter((email: string) => emails.some((e) => e === email)),
        statement: values.statement?.filter((email: string) => emails.some((e) => e === email)),
      },
      {
        keepDirty: true,
      }
    );
    await trigger('contacts');
  };

  const deleteContact = async (id: string) => {
    const index = contacts.findIndex((contact) => contact.id === id);
    setDeleteIndex(index);
    const updatedContacts = contacts.filter((contact) => contact.id !== id);
    const updatedData = getValues();
    if (updatedContacts.length > 0) {
      let newIndex = updatedContacts.length - 1;
      if (index == 0) newIndex = 1;
      else newIndex = index - 1;
      setActiveContactId(contacts[newIndex].id);
    } else {
      setActiveContactId('');
    }

    const updatedValue = {
      ...updatedData,
      contacts: (getValues('contacts') as ContactDetails[]).filter((_, i: number) => i !== index),
    };

    setContacts(updatedContacts);
    if (updatedContacts.length > 0) {
      const nextContactIndex = index < updatedContacts.length ? index + 1 : updatedContacts.length - 1;
      setActiveContactId(updatedContacts[nextContactIndex].id);
    } else {
      setActiveContactId('');
    }
    reset(updatedValue, {
      keepDirty: true,
    });
    await resetDataExchangeEmails();
  };

  useEffect(() => {
    if (mode === 'edit') {
      const contacts = getValues('contacts') as ContactDetails[] | undefined;
      if (contacts && contacts.length > 0) {
        const newContacts: Contact[] = contacts.map((details) => ({
          id: genUniqueId(),
          name: `${details.firstName ?? ''} ${details.lastName ?? ''}`.trim() || 'New Contact',
          data: details,
        }));
        if (searchParam.get('contactMode') === 'create')
          newContacts.push({
            id: genUniqueId(),
            name: 'New Contact',
            data: {} as ContactDetails,
          });
        setContacts(newContacts);
        const selectedContactId = searchParam.get('contactId')
          ? searchParam.get('contactId')
          : newContacts[newContacts.length - 1].id;
        if (selectedContactId) {
          const selectedContact = newContacts.find((contact) => contact.data.contactId === selectedContactId);
          if (selectedContact) setActiveContactId(selectedContact?.id);
        } else setActiveContactId(newContacts[0].id);
      }
    }
    if (mode === 'create') {
      const contacts = getValues('contacts') as ContactDetails[] | undefined;
      if (contacts && contacts.length > 0) {
        const newContacts: Contact[] = contacts.map((_, index) => ({
          id: genUniqueId(),
          name: contacts[index].firstName ? contacts[index].firstName + ' ' + contacts[index].lastName : 'New Contact',
          data: contacts[index],
        }));
        setContacts(newContacts);
        setActiveContactId(newContacts[newContacts.length - 1].id);
      }
    }
  }, [mode]);

  useEffect(() => {
    if (!isLoading) reset(getValues(), { keepDirty: isDirty, keepTouched: isDirty });
  }, [isLoading]);

  return (
    <Fragment key={activeContactId}>
      {(() => {
        const currentContactIndex = contacts.findIndex((person) => person.id === activeContactId);

        if (currentContactIndex === -1) {
          let newIndex = contacts.length - 1;
          if (deleteIndex)
            if (deleteIndex == 0) newIndex = 1;
            else newIndex = deleteIndex - 1;
          setActiveContactId(contacts[newIndex].id);
          return null;
        }
        return (
          <div className="flex flex-col gap-8 mt-10 min-h-[calc(100vh-45vh)] ">
            <Loader isLoading={isLoading || isFetching} className="h-full flex min-h-[38vh]">
              <div className="bg-[#F5F5F5] h-12 flex overflow-scroll scrollbar-hide border-b-[1px] border-bordercolor">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => handleContactSwitch(contact.id)}
                    className={`p-2 border-r-[1px] border-bordercolor inline-flex flex-row items-center shrink-0 ${
                      contacts.length > 1 ? 'justify-between' : 'justify-start'
                    } gap-2 rounded-none whitespace-nowrap ${
                      activeContactId === contact.id
                        ? 'bg-[#EDF5FF] text-primary'
                        : 'bg-[#E2E2E2] text-primary cursor-pointer'
                    }`}
                  >
                    <div>
                      <User className="h-6 w-6" />
                    </div>
                    <span className="font-medium">{contact.name}</span>
                    {contacts.length > 1 && activeContactId === contact.id && !contact.data.contactId && (
                      <div className="flex align-bottom">
                        <Trash2
                          color="#D72222"
                          className="w-4 h-4 cursor-pointer"
                          strokeWidth="1"
                          onClick={() => deleteContact(activeContactId)}
                        />
                      </div>
                    )}
                  </div>
                ))}
                <Button
                  onClick={addContact}
                  className="p-2 bg-primary h-12 w-12 text-white flex justify-center items-center rounded-none"
                >
                  <Plus />
                </Button>
              </div>

              {contacts.length > 0 ? (
                contactFor === 'CLIENT' ? (
                  <ClientContactFormSection
                    contacts={contacts}
                    currentContactIndex={currentContactIndex}
                    activeContactId={activeContactId}
                    setContacts={setContacts}
                    resetDataExchangeEmails={resetDataExchangeEmails}
                    type={type}
                  />
                ) : contactFor === 'VENDOR' ? (
                  <VendorContactFormSection
                    contacts={contacts}
                    currentContactIndex={currentContactIndex}
                    activeContactId={activeContactId}
                    setContacts={setContacts}
                  />
                ) : null
              ) : (
                <div className="flex justify-center items-center h-full w-full">No Contacts Available</div>
              )}
            </Loader>
          </div>
        );
      })()}
    </Fragment>
  );
};
